import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * `'channel'` unifies what used to be two separate drawer kinds (`'panel'` /
 * `'influencer-chat'`) into one drawer with a left tab rail — see
 * `ChannelDrawer.tsx`. `initialSection` picks which tab is active when the
 * drawer (re-)opens; `initialTab` still threads through to the Channel-detail
 * side's own internal sub-tab (Overview/Channels/Statistics/...).
 *
 * `'add-influencers'` is unrelated to a specific channel (campaign-level
 * candidate search), so it stays a separate, tab-less drawer content — see
 * `ChannelDrawer`'s `addInfluencers` prop for the extra data it needs.
 */
export type ChannelDrawerState =
  | null
  | { kind: 'channel'; userId: string; initialSection: 'details' | 'chat'; initialTab?: string; campaignId?: string }
  | { kind: 'add-influencers'; campaignId: string };

export function useChannelDrawer() {
  const [drawer, setDrawer] = useState<ChannelDrawerState>(null);
  const [drawerEntered, setDrawerEntered] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeDrawer = useCallback(() => {
    setDrawerEntered(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setDrawer(null);
    }, 280);
  }, []);

  useEffect(() => {
    if (!drawer) {
      setDrawerEntered(false);
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      return;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setDrawerEntered(false);
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setDrawerEntered(true));
    });
    return () => cancelAnimationFrame(id);
  }, [drawer]);

  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawer, closeDrawer]);

  // Names/signatures kept identical to before the Channel-detail/Chat-system
  // merge, so every existing call site (ChannelNameQuickButtons,
  // ChannelLinksPopover, and the pages wiring them) needed zero changes —
  // only the drawer state shape they produce changed internally.
  const openPanelDrawer = useCallback((userId: string, initialTab?: string) => {
    setDrawer({ kind: 'channel', userId, initialSection: 'details', initialTab });
  }, []);

  // Open the channel drawer directly on Channel detail's "stats" sub-tab.
  const openStatsDrawer = useCallback((userId: string) => {
    setDrawer({ kind: 'channel', userId, initialSection: 'details', initialTab: 'stats' });
  }, []);

  const openChatDrawer = useCallback((userId: string, campaignId?: string) => {
    setDrawer({ kind: 'channel', userId, initialSection: 'chat', campaignId });
  }, []);

  const openAddInfluencersDrawer = useCallback((campaignId: string) => {
    setDrawer({ kind: 'add-influencers', campaignId });
  }, []);

  return {
    drawer,
    drawerEntered,
    closeDrawer,
    openPanelDrawer,
    openStatsDrawer,
    openChatDrawer,
    openAddInfluencersDrawer,
  };
}
